// src/pages/finance-portal/tp-adjustments/TpAdjustmentForm.js
//
// Create / view / edit a TP adjustment.
// Includes a live "Compute" preview before save so users see the calc result.

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Box, Typography, Paper, Button, TextField, IconButton, Card, Chip, Alert,
    MenuItem, Select, FormControl, InputLabel, CircularProgress, Skeleton,
    InputAdornment, Tooltip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import CalculateIcon from '@mui/icons-material/Calculate';
import SendIcon from '@mui/icons-material/Send';
import CheckIcon from '@mui/icons-material/Check';
import BlockIcon from '@mui/icons-material/Block';
import PostAddIcon from '@mui/icons-material/PostAdd';
import DeleteIcon from '@mui/icons-material/Delete';
import { useSnackbar } from 'notistack';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

const STATUS_COLORS = {
    DRAFT:            { bg: '#f1f5f9', color: '#64748b' },
    PENDING_APPROVAL: { bg: '#fef9c3', color: '#854d0e' },
    APPROVED:         { bg: '#dbeafe', color: '#1d4ed8' },
    POSTED:           { bg: '#dcfce7', color: '#166534' },
    REJECTED:         { bg: '#fecaca', color: '#991b1b' },
};

function fmt(val, currency = 'USD', decimals = 2) {
    if (val == null) return '—';
    return new Intl.NumberFormat('en-US', {
        style: 'currency', currency, maximumFractionDigits: decimals,
    }).format(val);
}

export default function TpAdjustmentForm() {
    const navigate = useNavigate();
    const { adjId } = useParams();
    const isEdit = !!adjId;
    const { enqueueSnackbar } = useSnackbar();

    const [policies, setPolicies] = useState([]);
    const [data, setData] = useState({
        policy_id: '', period_start: '', period_end: '',
        cost_base_amount: '', actual_amount_charged: '',
        rationale: '',
    });
    const [adjustment, setAdjustment] = useState(null);
    const [preview, setPreview] = useState(null);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(isEdit);
    const [computing, setComputing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const fetchPolicies = useCallback(async () => {
        try {
            const res = await axiosInstance.get('/api/finance/tp/policies',
                { params: { per_page: 200, status: 'ACTIVE' } });
            if (res.data?.success) setPolicies(res.data.data || []);
        } catch { /* silent */ }
    }, []);

    const fetchAdjustment = useCallback(async () => {
        if (!isEdit) return;
        setLoading(true);
        try {
            const res = await axiosInstance.get(`/api/finance/tp/adjustments/${adjId}`);
            if (res.data.success) {
                const a = res.data.data;
                setAdjustment(a);
                setData({
                    policy_id: a.policy_id || '',
                    period_start: a.period_start || '',
                    period_end: a.period_end || '',
                    cost_base_amount: String(a.cost_base_amount ?? ''),
                    actual_amount_charged: String(a.actual_amount_charged ?? ''),
                    rationale: a.rationale || '',
                });
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load');
        } finally {
            setLoading(false);
        }
    }, [isEdit, adjId]);

    useEffect(() => { fetchPolicies(); fetchAdjustment(); }, [fetchPolicies, fetchAdjustment]);

    const update = (k, v) => {
        setData(prev => ({ ...prev, [k]: v }));
        if (errors[k]) setErrors(prev => { const n = { ...prev }; delete n[k]; return n; });
        setPreview(null);  // invalidate any prior preview
    };

    const isReadOnly = adjustment && adjustment.status !== 'DRAFT' && adjustment.status !== 'REJECTED';

    const validateLocal = () => {
        const e = {};
        if (!data.policy_id) e.policy_id = 'Required';
        if (!data.period_start) e.period_start = 'Required';
        if (!data.period_end) e.period_end = 'Required';
        if (data.period_start && data.period_end && data.period_end < data.period_start) {
            e.period_end = 'Must be on or after period start';
        }
        const cb = parseFloat(data.cost_base_amount);
        if (isNaN(cb) || cb < 0) e.cost_base_amount = 'Must be ≥ 0';
        const ac = parseFloat(data.actual_amount_charged);
        if (isNaN(ac)) e.actual_amount_charged = 'Required';
        return e;
    };

    const compute = async () => {
        const localErrors = validateLocal();
        if (Object.keys(localErrors).length > 0) {
            setErrors(localErrors);
            enqueueSnackbar('Fill all required fields first', { variant: 'warning' });
            return;
        }
        setComputing(true);
        try {
            const res = await axiosInstance.post('/api/finance/tp/adjustments/compute', {
                policy_id: parseInt(data.policy_id),
                period_start: data.period_start,
                period_end: data.period_end,
                cost_base_amount: parseFloat(data.cost_base_amount),
                actual_amount_charged: parseFloat(data.actual_amount_charged),
            });
            if (res.data?.success) {
                setPreview(res.data.data);
                enqueueSnackbar('Calculated', { variant: 'success' });
            }
        } catch (err) {
            const errs = err.response?.data?.errors;
            if (errs) {
                setErrors(errs);
                enqueueSnackbar('Validation failed', { variant: 'warning' });
            } else {
                enqueueSnackbar(err.response?.data?.message || 'Failed', { variant: 'error' });
            }
        } finally {
            setComputing(false);
        }
    };

    const handleSave = async () => {
        const localErrors = validateLocal();
        if (Object.keys(localErrors).length > 0) {
            setErrors(localErrors);
            enqueueSnackbar('Fix the highlighted errors', { variant: 'warning' });
            return;
        }
        setSaving(true);
        setErrors({});
        try {
            const payload = {
                policy_id: parseInt(data.policy_id),
                period_start: data.period_start,
                period_end: data.period_end,
                cost_base_amount: parseFloat(data.cost_base_amount),
                actual_amount_charged: parseFloat(data.actual_amount_charged),
                rationale: data.rationale?.trim() || null,
            };
            let id = adjId;
            if (isEdit) {
                await axiosInstance.put(`/api/finance/tp/adjustments/${adjId}`, payload);
                enqueueSnackbar('Adjustment updated', { variant: 'success' });
            } else {
                const res = await axiosInstance.post('/api/finance/tp/adjustments', payload);
                id = res.data?.data?.id;
                enqueueSnackbar('Adjustment created (DRAFT)', { variant: 'success' });
            }
            navigate(`/finance-portal/tp-adjustments/${id}`);
        } catch (err) {
            const serverErrors = err.response?.data?.errors;
            if (serverErrors && typeof serverErrors === 'object') {
                setErrors(serverErrors);
                enqueueSnackbar('Validation failed', { variant: 'warning' });
            } else {
                enqueueSnackbar(err.response?.data?.message || 'Failed to save', { variant: 'error' });
            }
        } finally {
            setSaving(false);
        }
    };

    const action = async (path, body) => {
        try {
            const res = await axiosInstance.post(
                `/api/finance/tp/adjustments/${adjId}/${path}`, body || {});
            enqueueSnackbar(res.data?.message || 'Done', { variant: 'success' });
            fetchAdjustment();
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || 'Failed', { variant: 'error' });
        }
    };

    const submitReject = async () => {
        const reason = window.prompt('Rejection reason:');
        if (!reason) {
            enqueueSnackbar('Reason is required', { variant: 'warning' });
            return;
        }
        await action('reject', { reason });
    };

    const deleteAdjustment = async () => {
        if (!window.confirm('Delete this adjustment?')) return;
        try {
            await axiosInstance.delete(`/api/finance/tp/adjustments/${adjId}`);
            enqueueSnackbar('Adjustment deleted', { variant: 'success' });
            navigate('/finance-portal/tp-adjustments');
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || 'Failed', { variant: 'error' });
        }
    };

    const fieldSx = { fontFamily, fontSize: '0.85rem', borderRadius: '10px' };
    const labelSx = { fontFamily, fontSize: '0.85rem' };

    if (loading) return <Box sx={{ p: 4, fontFamily }}><Skeleton height={400} /></Box>;

    const sStyle = adjustment && (STATUS_COLORS[adjustment.status] || STATUS_COLORS.DRAFT);
    // Show stored values when read-only / for displaying past calculation
    const displayValues = adjustment || {};

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            <Paper elevation={0} sx={{
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3,
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <IconButton onClick={() => navigate('/finance-portal/tp-adjustments')}
                                sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.15)',
                                      '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Box>
                        <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.85)', fontSize: '0.78rem' }}>
                            TP Year-End Adjustment
                        </Typography>
                        <Typography variant="h5" sx={{ fontFamily, fontWeight: 800, color: 'white' }}>
                            {isEdit && adjustment ? adjustment.adjustment_number : 'New Adjustment'}
                        </Typography>
                        {adjustment && (
                            <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.85)', fontSize: '0.85rem', mt: 0.25 }}>
                                {adjustment.policy_number} · {adjustment.policy_name}
                            </Typography>
                        )}
                    </Box>
                </Box>
                {adjustment && (
                    <Box sx={{ mt: 1 }}>
                        <Chip label={adjustment.status?.replace('_', ' ')} size="small"
                              sx={{ fontFamily, fontWeight: 700, fontSize: '0.7rem',
                                    bgcolor: sStyle.bg, color: sStyle.color }} />
                    </Box>
                )}
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1100, mx: 'auto' }}>
                {error && <Alert severity="error" sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{error}</Alert>}

                {adjustment && adjustment.status === 'REJECTED' && adjustment.rejection_reason && (
                    <Alert severity="error" sx={{ mb: 2, borderRadius: '12px', fontFamily }}>
                        <strong>Rejected:</strong> {adjustment.rejection_reason}
                    </Alert>
                )}

                {/* Action bar */}
                {isEdit && adjustment && (
                    <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                        {adjustment.status === 'DRAFT' && (
                            <Button startIcon={<SendIcon />} variant="contained" size="small"
                                    onClick={() => action('submit')}
                                    sx={{ fontFamily, fontWeight: 600, textTransform: 'none',
                                          borderRadius: '10px', bgcolor: '#3b82f6' }}>
                                Submit for Approval
                            </Button>
                        )}
                        {adjustment.status === 'PENDING_APPROVAL' && (
                            <>
                                <Button startIcon={<CheckIcon />} variant="contained" size="small"
                                        onClick={() => action('approve')}
                                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none',
                                              borderRadius: '10px', bgcolor: '#16a34a' }}>
                                    Approve
                                </Button>
                                <Button startIcon={<BlockIcon />} variant="outlined" size="small"
                                        onClick={submitReject}
                                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none',
                                              borderRadius: '10px', color: '#ef4444', borderColor: '#fecaca' }}>
                                    Reject
                                </Button>
                            </>
                        )}
                        {adjustment.status === 'APPROVED' && !adjustment.in_range && (
                            <Button startIcon={<PostAddIcon />} variant="contained" size="small"
                                    onClick={() => action('post')}
                                    sx={{ fontFamily, fontWeight: 600, textTransform: 'none',
                                          borderRadius: '10px', bgcolor: '#22c55e' }}>
                                Post
                            </Button>
                        )}
                        {(adjustment.status === 'DRAFT' || adjustment.status === 'REJECTED') && (
                            <Button startIcon={<DeleteIcon />} variant="text" size="small"
                                    onClick={deleteAdjustment}
                                    sx={{ fontFamily, textTransform: 'none', color: '#94a3b8' }}>
                                Delete
                            </Button>
                        )}
                    </Box>
                )}

                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', p: 3, mb: 2 }}>
                    <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.95rem', mb: 2 }}>
                        Inputs
                    </Typography>
                    <Box sx={{ display: 'grid',
                              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 1.5 }}>
                        <FormControl size="small">
                            <InputLabel sx={labelSx}>Active Policy *</InputLabel>
                            <Select value={data.policy_id} label="Active Policy *"
                                    onChange={e => update('policy_id', e.target.value)}
                                    disabled={isReadOnly || isEdit}
                                    error={!!errors.policy_id}
                                    sx={{ fontFamily, borderRadius: '10px' }}>
                                {policies.map(p =>
                                    <MenuItem key={p.id} value={p.id} sx={{ fontFamily }}>
                                        {p.policy_number} · {p.name} ({p.from_entity_name} → {p.to_entity_name})
                                    </MenuItem>)}
                                {/* When editing, show the linked policy even if not in the active list */}
                                {isEdit && adjustment?.policy_id && !policies.some(p => p.id === adjustment.policy_id) && (
                                    <MenuItem value={adjustment.policy_id} sx={{ fontFamily }} disabled>
                                        {adjustment.policy_number} · {adjustment.policy_name} (linked)
                                    </MenuItem>
                                )}
                            </Select>
                        </FormControl>
                        <Box sx={{ display: 'flex', gap: 1.5 }}>
                            <TextField label="Period Start *" type="date" size="small" fullWidth
                                       value={data.period_start}
                                       onChange={e => update('period_start', e.target.value)}
                                       error={!!errors.period_start}
                                       helperText={errors.period_start || ' '}
                                       InputLabelProps={{ shrink: true, sx: labelSx }}
                                       InputProps={{ sx: fieldSx, readOnly: isReadOnly }} />
                            <TextField label="Period End *" type="date" size="small" fullWidth
                                       value={data.period_end}
                                       onChange={e => update('period_end', e.target.value)}
                                       error={!!errors.period_end}
                                       helperText={errors.period_end || ' '}
                                       InputLabelProps={{ shrink: true, sx: labelSx }}
                                       InputProps={{ sx: fieldSx, readOnly: isReadOnly }} />
                        </Box>
                        <TextField label="Cost Base Amount *" size="small" type="number"
                                   value={data.cost_base_amount}
                                   onChange={e => update('cost_base_amount', e.target.value)}
                                   error={!!errors.cost_base_amount}
                                   helperText={errors.cost_base_amount || 'Allocable cost base for the period'}
                                   InputProps={{
                                       startAdornment: <InputAdornment position="start"
                                           sx={{ '& .MuiTypography-root': { fontFamily, fontSize: '0.85rem' } }}>$</InputAdornment>,
                                       sx: fieldSx, readOnly: isReadOnly,
                                   }}
                                   InputLabelProps={{ sx: labelSx }}
                                   FormHelperTextProps={{ sx: { fontFamily, fontSize: '0.7rem' } }} />
                        <TextField label="Actual Amount Charged *" size="small" type="number"
                                   value={data.actual_amount_charged}
                                   onChange={e => update('actual_amount_charged', e.target.value)}
                                   error={!!errors.actual_amount_charged}
                                   helperText={errors.actual_amount_charged || 'Net IC charge for the period'}
                                   InputProps={{
                                       startAdornment: <InputAdornment position="start"
                                           sx={{ '& .MuiTypography-root': { fontFamily, fontSize: '0.85rem' } }}>$</InputAdornment>,
                                       sx: fieldSx, readOnly: isReadOnly,
                                   }}
                                   InputLabelProps={{ sx: labelSx }}
                                   FormHelperTextProps={{ sx: { fontFamily, fontSize: '0.7rem' } }} />
                    </Box>
                    <TextField label="Rationale" size="small" fullWidth multiline rows={3} sx={{ mt: 1.5 }}
                               value={data.rationale}
                               onChange={e => update('rationale', e.target.value)}
                               InputProps={{ sx: fieldSx, readOnly: isReadOnly }}
                               InputLabelProps={{ sx: labelSx }} />
                    {!isReadOnly && (
                        <Box sx={{ mt: 2 }}>
                            <Tooltip title="Run the calculator without saving">
                                <Button startIcon={<CalculateIcon />} variant="outlined" size="small"
                                        onClick={compute} disabled={computing}
                                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px' }}>
                                    {computing ? 'Computing...' : 'Compute Preview'}
                                </Button>
                            </Tooltip>
                        </Box>
                    )}
                </Card>

                {(preview || (isEdit && adjustment)) && (
                    <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', p: 3, mb: 2 }}>
                        <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.95rem', mb: 2 }}>
                            {preview ? 'Preview' : 'Computation'}
                        </Typography>
                        <Box sx={{ display: 'grid',
                                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                                  gap: 2 }}>
                            {(() => {
                                const v = preview || displayValues;
                                const cur = preview ? preview.policy?.currency : displayValues.currency;
                                const targetPct = preview ? preview.target_pct : displayValues.target_pct_at_adjustment;
                                const armLow = preview ? preview.arm_length_low_pct : null;
                                const armHigh = preview ? preview.arm_length_high_pct : null;
                                return [
                                    { label: 'Actual %',
                                      value: v.actual_pct != null ? `${Number(v.actual_pct).toFixed(2)}%` : '—',
                                      color: v.in_range ? '#16a34a' : '#dc2626' },
                                    { label: 'Target %',
                                      value: targetPct != null ? `${Number(targetPct).toFixed(2)}%` : '—',
                                      color: '#0f172a' },
                                    { label: 'Arm-Length Range',
                                      value: armLow != null && armHigh != null
                                          ? `${Number(armLow).toFixed(2)}% – ${Number(armHigh).toFixed(2)}%`
                                          : (preview ? '—' : 'See policy'),
                                      color: '#475569' },
                                    { label: 'Target Amount',
                                      value: fmt(v.target_amount, cur), color: '#0f172a' },
                                    { label: 'Actual Charged',
                                      value: fmt(preview ? preview.actual_amount_charged : displayValues.actual_amount_charged, cur),
                                      color: '#0f172a' },
                                    { label: 'Adjustment',
                                      value: fmt(v.adjustment_amount, cur),
                                      color: v.in_range ? '#16a34a'
                                          : (v.adjustment_amount > 0 ? '#16a34a' : '#dc2626') },
                                ].map(k => (
                                    <Box key={k.label}>
                                        <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#94a3b8',
                                                          textTransform: 'uppercase', fontWeight: 600 }}>
                                            {k.label}
                                        </Typography>
                                        <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1.1rem',
                                                          color: k.color }}>
                                            {k.value}
                                        </Typography>
                                    </Box>
                                ));
                            })()}
                        </Box>
                        {(preview?.in_range || displayValues.in_range) && (
                            <Alert severity="success" sx={{ mt: 2, borderRadius: '10px', fontFamily, fontSize: '0.82rem' }}>
                                Actual is within the arm-length range — no adjustment needed.
                            </Alert>
                        )}
                    </Card>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
                    <Button onClick={() => navigate('/finance-portal/tp-adjustments')}
                            sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>
                        {isReadOnly ? 'Back' : 'Cancel'}
                    </Button>
                    {!isReadOnly && (
                        <Button variant="contained" onClick={handleSave} disabled={saving}
                                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                                sx={{ fontFamily, fontWeight: 600, textTransform: 'none',
                                      borderRadius: '10px', bgcolor: ACCENT,
                                      '&:hover': { bgcolor: '#16a34a' } }}>
                            {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Adjustment'}
                        </Button>
                    )}
                </Box>
            </Box>
        </Box>
    );
}
